import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHotel,
  faHome,
  faBuilding,
  faCity,
  faUmbrellaBeach,
  faCampground,
  faHouseUser,
  faShip,
  faUser,
  faEnvelope,
  faPhone,
  faLock,
  faPlus,
  faTrashAlt,
  faPen,
  faBed,
  faImages,
  faCloudUploadAlt,
  faTimes,
  faIdCard,
  faFileSignature,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from '../../App';
import useHotelList from '../../hooks/useHotelList';
import useCustomToast from '../../hooks/useCustomToast';
import AMENITIES_OPTIONS from '../../config/amenitiesOptions';
import { buildPropertyPayload, searchRoomCategories, fetchHostProfileFromToken, getDecryptedToken, isAlreadyRegisteredMessage } from '../../api/onboardingApi';
import { submitOnboarding } from './onboardingSubmit';
import { validateRooms, isValidEmail, isValidPhone } from './useOnboardingValidation';
import { signupHost, sendSignupOtp, verifySignupOtp } from '../../api/authHandlers';
import { verifyGst } from '../../api/authHandlers';
import { useSubmitState } from './useSubmitState';
import { buildOnboardingSummary } from './onboardingSummary';
import {
  draftStore,
  STEPS,
  LAST_STEP,
  clearListingDraft,
  getFirstStep,
  getTotalSteps,
  getWizardSteps,
  getStepOrdinal,
  markAccountComplete,
  isAccountCreatedFor,
  prepareDraftStore,
  saveCurrentStep,
  computeInitialStep,
  canSkipAccountStep,
  shouldShowResumeBanner,
  createEmptyRoom,
  loadStoredRooms,
} from './onboardingDraft';
import { HOTEL_IMAGE_SECTIONS, KYC_IMAGE_SECTIONS, MIN_MAIN_IMAGES, MIN_PROPERTY_IMAGES } from './onboardingConfig';
import OnboardingSuccess from './OnboardingSuccess';
import SubmitButton from './SubmitButton';
import ListingHeader from './ListingHeader';
import ListingStepper from './ListingStepper';
import ListingSupportBox from './ListingSupportBox';
import './ListingPage.css';

let imageSeq = 0;
function makeImageEntry(file) {
  imageSeq += 1;
  return {
    id: `img_${Date.now().toString(36)}_${imageSeq}`,
    file,
    previewUrl: URL.createObjectURL(file),
    name: file.name,
    size: file.size,
  };
}

function countImages(pending) {
  return Object.values(pending || {}).reduce((sum, list) => sum + (Array.isArray(list) ? list.length : 0), 0);
}

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Update a single guest price box only — no autofill across boxes. */
function applyOccupancyRateChange(prevRates, guestIndex, rawValue, occupancy) {
  const occ = Math.max(1, parseInt(occupancy, 10) || 1);
  const next = { ...(prevRates || {}) };
  for (let i = 1; i <= occ; i += 1) {
    if (next[`${i}guest`] === undefined) next[`${i}guest`] = '';
  }
  next[`${guestIndex}guest`] = String(rawValue || '').replace(/[^\d.]/g, '');
  return next;
}

function resizeOccupancyRates(prevRates, occupancy) {
  const occ = Math.max(1, Math.min(20, parseInt(occupancy, 10) || 1));
  const next = {};
  for (let i = 1; i <= occ; i += 1) {
    next[`${i}guest`] = (prevRates && prevRates[`${i}guest`]) || '';
  }
  return next;
}

const PROPERTY_TYPES = [
  { id: 'hotel', name: 'Hotel', icon: faHotel, description: 'Traditional hotel accommodation' },
  { id: 'villa', name: 'Villa', icon: faHome, description: 'Luxury private villa' },
  { id: 'bungalow', name: 'Bungalow', icon: faBuilding, description: 'Single-story detached house' },
  { id: 'apartment', name: 'Apartment', icon: faCity, description: 'Multi-unit residential building' },
  { id: 'resort', name: 'Resort', icon: faUmbrellaBeach, description: 'Full-service vacation destination' },
  { id: 'tent', name: 'Tent', icon: faCampground, description: 'Glamping and camping experience' },
  { id: 'homestay', name: 'Homestay', icon: faHouseUser, description: 'Local family accommodation' },
  { id: 'boathouse', name: 'Boathouse', icon: faShip, description: 'Waterfront floating accommodation' },
];

const ListingPage = ({ mode = 'signup', skipOtp = false }) => {
  const isAddProperty = mode === 'add-property';
  const firstStep = getFirstStep(mode);
  const totalSteps = getTotalSteps(mode);
  const [currentStep, setCurrentStep] = useState(firstStep);
  const [maxReachedStep, setMaxReachedStep] = useState(firstStep);
  const [draftReady, setDraftReady] = useState(false);
  const [canSkipAccount, setCanSkipAccount] = useState(isAddProperty);
  const [showResumeBanner, setShowResumeBanner] = useState(false);

  // Step 1: Account Setup
  const [fullName, setFullName] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [accountPhone, setAccountPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Step 2: Property Type
  const [propertyName, setPropertyName] = useState('');
  const [selectedPropertyType, setSelectedPropertyType] = useState('');

  // Step 3: Location
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('India');
  const [pincode, setPincode] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [placeSearchInput, setPlaceSearchInput] = useState('');
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const autocompleteInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const inlineMapContainerRef = useRef(null);

  // Step 4: Description & Contact
  const [propertyDescription, setPropertyDescription] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [checkinTime, setCheckinTime] = useState('12:00');
  const [checkoutTime, setCheckoutTime] = useState('11:00');

  // Step 5: Amenities & Policies
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [selectedHotelPolicies, setSelectedHotelPolicies] = useState([]);
  const [selectedCancellationPolicies, setSelectedCancellationPolicies] = useState([]);
  const [isOpen24x7, setIsOpen24x7] = useState(false);
  const [isCoupleFriendly, setIsCoupleFriendly] = useState(false);

  // Step 8: KYC & Compliance (text fields persisted; photos held in memory like property images)
  const [panNumber, setPanNumber] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [gstActive, setGstActive] = useState('');
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  const [kycTouched, setKycTouched] = useState(false);
  const [gstVerifying, setGstVerifying] = useState(false);
  const [gstVerifiedData, setGstVerifiedData] = useState(null);
  const [gstLocked, setGstLocked] = useState(false);
  const [gstError, setGstError] = useState('');

  // Step 6: Room Setup — one entry per room type; only the active one is expanded
  const [rooms, setRooms] = useState(() => [createEmptyRoom()]);
  const [activeRoomIndex, setActiveRoomIndex] = useState(0);
  const [roomSearchResults, setRoomSearchResults] = useState([]);
  const [roomSearchOpen, setRoomSearchOpen] = useState(false);
  const [roomSearchLoading, setRoomSearchLoading] = useState(false);
  const [rateBlurWarnings, setRateBlurWarnings] = useState({});
  const roomSearchRef = useRef(null);
  const activeRoom = rooms[activeRoomIndex] || rooms[0];
  const activeRoomName = activeRoom?.roomTypeName || '';
  const activeRoomCategoryId = activeRoom?.roomTypeCategoryId || '';

  // Step 7: Property Images — kept in memory and posted to the dashboard's
  // updateImage API once the property exists (it needs a hotel_id).
  const [pendingImages, setPendingImages] = useState({});
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [dragOverSection, setDragOverSection] = useState('');
  const imageInputRefs = useRef({});
  const pendingImagesRef = useRef({});

  // Step 6 room images — keyed by room.id, held in memory (File objects).
  const [roomImages, setRoomImages] = useState({});
  const roomImageInputRefs = useRef({});
  const roomImagesRef = useRef({});

  // Validation states
  const [step1Touched, setStep1Touched] = useState(false);
  const [step2Touched, setStep2Touched] = useState(false);
  const [step3Touched, setStep3Touched] = useState(false);
  const [step4Touched, setStep4Touched] = useState(false);
  const [step5Touched, setStep5Touched] = useState(false);
  const [step6Touched, setStep6Touched] = useState(false);
  const [step7Touched, setStep7Touched] = useState(false);
  const [createdHotel, setCreatedHotel] = useState(null);
  /** After setHotels succeeds but image upload fails, retry Submit without creating again. */
  const [pendingHotelId, setPendingHotelId] = useState(null);

  // OTP verification state (email only)
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpTarget, setOtpTarget] = useState(null); // 'email' only
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);

  const navigate = useNavigate();
  const { handleInvalidToken } = useContext(AuthContext);
  const { refetch } = useHotelList();
  const { successToast, errorToast } = useCustomToast();
  const { submitting, submitError, success, startSubmit, setSubmitError, setSuccess, reset } = useSubmitState();

  const amenitiesOptions = AMENITIES_OPTIONS;

  const hotelPoliciesOptions = [
    'Valid government ID required at check-in',
    'No smoking inside rooms',
    'Pets not allowed',
    'Outside food allowed',
    'Outside food not allowed',
    'Loud music not allowed after 10 PM',
    'Visitors allowed till 10 PM',
    'Early check-in subject to availability',
    'Late check-out subject to availability',
    'Local ID accepted',
    'Unmarried couples allowed',
    'Children below 6 stay free without extra bed',
    'Extra bed available at additional cost'
  ];

  const cancellationPoliciesOptions = [
    'Free cancellation up to 24 hours before check-in',
    'Free cancellation up to 48 hours before check-in',  
    'No refund on cancellation within 24 hours of check-in',
    'No-show will be charged 100% of booking amount',
    'Partial refund for early check-out is not applicable',
    'Date change is subject to availability and fare difference',
    'Non-refundable booking'
  ];

  const roomAmenitiesOptions = [
    'Air Conditioning','Wifi','TV','Mini Bar','Safe','Tea/Coffee Maker','Balcony','Bathtub','Shower','Hair Dryer',
    'Iron/Ironing Board','Workspace','Sofa','Kitchenette','Fridge','Microwave','Room Service','Daily Housekeeping'
  ];

  const propertyTypes = PROPERTY_TYPES;

  // Load saved draft (localStorage via draftStore — survives reload, new tab and logout-induced draftStore.clear())
  useEffect(() => {
    prepareDraftStore();
    const loadString = (key, setter) => {
      const value = draftStore.getItem(key);
      if (value != null) setter(value);
    };
    const savedAccountEmail = draftStore.getItem('accountEmail');
    const verifiedEmail = draftStore.getItem('verifiedEmail');
    const emailVerifiedSaved = draftStore.getItem('emailVerifiedSaved') === 'true';
    if (savedAccountEmail && emailVerifiedSaved && savedAccountEmail === verifiedEmail) {
      setEmailVerified(true);
    }
    const loadBool = (key, setter) => {
      const value = draftStore.getItem(key);
      if (value != null) setter(value === 'true');
    };
    const loadJson = (key, setter) => {
      const value = draftStore.getItem(key);
      if (value != null) {
        try { setter(JSON.parse(value)); } catch {}
      }
    };

    loadString('fullName', setFullName);
    loadString('accountEmail', setAccountEmail);
    loadString('accountPhone', setAccountPhone);
    loadString('password', setPassword);
    loadString('confirmPassword', setConfirmPassword);
    loadString('propertyName', setPropertyName);
    loadString('selectedPropertyType', setSelectedPropertyType);
    loadString('address', setAddress);
    loadString('city', setCity);
    loadString('state', setState);
    loadString('country', setCountry);
    loadString('pincode', setPincode);
    loadString('latitude', setLatitude);
    loadString('longitude', setLongitude);
    loadString('placeSearchInput', setPlaceSearchInput);
    loadString('propertyDescription', setPropertyDescription);
    loadString('phoneNumber', setPhoneNumber);
    loadString('email', setEmail);
    loadString('checkinTime', setCheckinTime);
    loadString('checkoutTime', setCheckoutTime);
    loadJson('selectedAmenities', setSelectedAmenities);
    loadJson('selectedHotelPolicies', setSelectedHotelPolicies);
    loadJson('selectedCancellationPolicies', setSelectedCancellationPolicies);
    loadBool('isOpen24x7', setIsOpen24x7);
    loadBool('isCoupleFriendly', setIsCoupleFriendly);
    loadString('panNumber', setPanNumber);
    loadString('gstNumber', setGstNumber);
    loadString('gstActive', (value) => {
      setGstActive(value);
      if (String(value).toLowerCase() === 'active') setGstLocked(true);
    });
    loadBool('agreedToPolicy', setAgreedToPolicy);
    const storedRooms = loadStoredRooms();
    if (storedRooms.length > 0) {
      setRooms(storedRooms);
      setActiveRoomIndex(0);
    }

    const tokenPresent = !!Cookies.get('token');
    const skipAccount = canSkipAccountStep(mode, tokenPresent);
    setCanSkipAccount(skipAccount);

    const resumeAt = computeInitialStep(mode, { tokenPresent });
    setCurrentStep(resumeAt);
    setMaxReachedStep((prev) => Math.max(prev, resumeAt));
    if (shouldShowResumeBanner(mode, resumeAt)) setShowResumeBanner(true);
    setDraftReady(true);
  }, [mode, firstStep, isAddProperty]);

  // Logged-in add property: prefill host fields for setHotels payload
  useEffect(() => {
    if (!isAddProperty) return undefined;
    let cancelled = false;
    (async () => {
      const token = getDecryptedToken();
      if (!token) return;
      const profile = await fetchHostProfileFromToken(token);
      if (!profile || cancelled) return;
      setFullName((v) => v || profile.name || '');
      setAccountEmail((v) => v || profile.email || '');
      setAccountPhone((v) => v || profile.phone || '');
      if (profile.host_id) localStorage.setItem('selectedHostId', String(profile.host_id));
    })();
    return () => { cancelled = true; };
  }, [isAddProperty]);

  useEffect(() => {
    if (!draftReady) return;
    saveCurrentStep(mode, currentStep);
    setMaxReachedStep((prev) => Math.max(prev, currentStep));
  }, [currentStep, mode, draftReady]);

  // Property contact is the host account — don't ask for it again on Details.
  useEffect(() => {
    if (accountPhone) setPhoneNumber(accountPhone);
  }, [accountPhone]);
  useEffect(() => {
    if (accountEmail) setEmail(accountEmail);
  }, [accountEmail]);

  // Persist to the draft store (localStorage)
  const persistString = (key, value) => {
    if (value != null && value !== '') draftStore.setItem(key, String(value));
  };

  useEffect(() => { persistString('fullName', fullName); }, [fullName]);
  useEffect(() => { persistString('accountEmail', accountEmail); }, [accountEmail]);
  useEffect(() => { persistString('accountPhone', accountPhone); }, [accountPhone]);
  useEffect(() => { persistString('password', password); }, [password]);
  useEffect(() => { persistString('confirmPassword', confirmPassword); }, [confirmPassword]);
  useEffect(() => { persistString('propertyName', propertyName); }, [propertyName]);
  useEffect(() => { persistString('selectedPropertyType', selectedPropertyType); }, [selectedPropertyType]);
  useEffect(() => { persistString('address', address); }, [address]);
  useEffect(() => { persistString('city', city); }, [city]);
  useEffect(() => { persistString('state', state); }, [state]);
  useEffect(() => { persistString('country', country); }, [country]);
  useEffect(() => { persistString('pincode', pincode); }, [pincode]);
  useEffect(() => { persistString('latitude', latitude); }, [latitude]);
  useEffect(() => { persistString('longitude', longitude); }, [longitude]);
  useEffect(() => { persistString('placeSearchInput', placeSearchInput); }, [placeSearchInput]);
  useEffect(() => { persistString('propertyDescription', propertyDescription); }, [propertyDescription]);
  useEffect(() => { persistString('phoneNumber', phoneNumber); }, [phoneNumber]);
  useEffect(() => { persistString('email', email); }, [email]);
  useEffect(() => { persistString('checkinTime', checkinTime); }, [checkinTime]);
  useEffect(() => { persistString('checkoutTime', checkoutTime); }, [checkoutTime]);
  // JSON/bool keys: wait for the draft to load so the initial empty state never overwrites it.
  useEffect(() => { if (draftReady) draftStore.setItem('selectedAmenities', JSON.stringify(selectedAmenities)); }, [selectedAmenities, draftReady]);
  useEffect(() => { if (draftReady) draftStore.setItem('selectedHotelPolicies', JSON.stringify(selectedHotelPolicies)); }, [selectedHotelPolicies, draftReady]);
  useEffect(() => { if (draftReady) draftStore.setItem('selectedCancellationPolicies', JSON.stringify(selectedCancellationPolicies)); }, [selectedCancellationPolicies, draftReady]);
  useEffect(() => { if (draftReady) draftStore.setItem('isOpen24x7', String(isOpen24x7)); }, [isOpen24x7, draftReady]);
  useEffect(() => { if (draftReady) draftStore.setItem('isCoupleFriendly', String(isCoupleFriendly)); }, [isCoupleFriendly, draftReady]);
  useEffect(() => { persistString('panNumber', panNumber); }, [panNumber]);
  useEffect(() => { persistString('gstNumber', gstNumber); }, [gstNumber]);
  useEffect(() => { persistString('gstActive', gstActive); }, [gstActive]);
  useEffect(() => { if (draftReady) draftStore.setItem('agreedToPolicy', String(agreedToPolicy)); }, [agreedToPolicy, draftReady]);
  useEffect(() => { if (draftReady) draftStore.setItem('roomTypes', JSON.stringify(rooms)); }, [rooms, draftReady]);

  // Auto-fill a 2-3 line description template from the property location when the
  // description is still empty. Never overwrites text the user already typed.
  const buildDescriptionTemplate = useCallback(() => {
    const typeLabel = propertyTypes.find((t) => t.id === selectedPropertyType)?.name || 'property';
    const name = propertyName.trim() || 'Our property';
    const loc = [city, state, country].map((p) => (p || '').trim()).filter(Boolean).join(', ');
    if (!loc) return '';
    return `Welcome to ${name}, a ${typeLabel.toLowerCase()} located in ${loc}. ` +
      `Enjoy a comfortable and memorable stay with modern amenities, warm hospitality and a prime location. ` +
      `Book your stay with us and experience the very best of ${city || state || 'the area'}.`;
  }, [propertyName, selectedPropertyType, city, state, country, propertyTypes]);

  useEffect(() => {
    if (!draftReady) return;
    if (propertyDescription.trim() === '' && (city.trim() || state.trim())) {
      const template = buildDescriptionTemplate();
      if (template) setPropertyDescription(template);
    }
  }, [draftReady, city, state, country, buildDescriptionTemplate, propertyDescription]);

  // Debounced room-type search
  useEffect(() => {
    const q = String(activeRoomName || '').trim();
    if (q.length < 2 || activeRoomCategoryId) {
      setRoomSearchResults([]);
      return undefined;
    }
    setRoomSearchLoading(true);
    const timer = setTimeout(async () => {
      const result = await searchRoomCategories(q);
      setRoomSearchLoading(false);
      if (result.ok) {
        setRoomSearchResults(result.data.slice(0, 40));
        setRoomSearchOpen(true);
      } else {
        setRoomSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [activeRoomName, activeRoomCategoryId]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (roomSearchRef.current && !roomSearchRef.current.contains(e.target)) {
        setRoomSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // OTP cooldown timer
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const id = setInterval(() => setOtpCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [otpCooldown]);

  // Re-show OTP input when user presses Continue without verifying email first
  const ensureVerification = () => {
    if (!isValidEmail(accountEmail)) { setStep1Touched(true); return false; }
    if (!isValidPhone(accountPhone)) { setStep1Touched(true); return false; }
    if (skipOtp) return true;
    if (!emailVerified) { setOtpTarget('email'); setStep1Touched(true); return false; }
    return true;
  };

  useEffect(() => {
    if (emailVerified) setStep1Touched(false);
  }, [emailVerified]);

  // Cleanup legacy draft keys on mount
  useEffect(() => {
    const legacyKeys = [
      'bankName','ifscCode','accountNumber','accountHolderName','panNumber','gstNumber','paymentMode',
      'hostName','hostEmail','hostPhone','hostGender','hotelOwnerFullName','hotelOwnerMobileNumber',
      'aadharCardNumber','rating'
    ];
    legacyKeys.forEach((key) => draftStore.removeItem(key));
  }, []);

  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setGoogleMapsLoaded(true);
      } else {
        setTimeout(checkGoogleMaps, 100);
      }
    };
    checkGoogleMaps();
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const styleTag = document.createElement('style');
    styleTag.innerHTML = '.pac-container{z-index:20000 !important;}';
    document.head.appendChild(styleTag);
    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  const extractAddressComponents = useCallback((addressComponents = []) => {
    const components = { address: '', city: '', state: '', country: '', pincode: '' };
    addressComponents.forEach((component) => {
      const types = component.types || [];
      if (types.includes('postal_code')) {
        components.pincode = component.long_name;
      } else if (types.includes('locality') || types.includes('administrative_area_level_3')) {
        components.city = component.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        components.state = component.long_name;
      } else if (types.includes('country')) {
        components.country = component.long_name;
      } else if (types.includes('premise') || types.includes('street_number') || types.includes('route')) {
        components.address = components.address ? `${component.long_name}, ${components.address}` : component.long_name;
      } else if (types.includes('sublocality_level_1') || types.includes('sublocality_level_2')) {
        components.address = components.address ? `${components.address}, ${component.long_name}` : component.long_name;
      }
    });
    return components;
  }, []);

  const applyLocationFromPlace = useCallback((place) => {
    if (!place) return;
    const lat = place.geometry?.location?.lat?.();
    const lng = place.geometry?.location?.lng?.();
    const placeName = place.name || '';
    const formattedAddress = place.formatted_address || '';
    const addressComponents = extractAddressComponents(place.address_components || []);

    if (lat != null) setLatitude(String(lat));
    if (lng != null) setLongitude(String(lng));
    setPlaceSearchInput(placeName ? `${placeName}, ${formattedAddress}` : formattedAddress || '');
    setState(addressComponents.state || '');
    setCountry(addressComponents.country || '');
  }, [extractAddressComponents]);

  const handleLocationSearchSubmit = useCallback((e) => {
    if (e.key !== 'Enter') return;
    if (!googleMapsLoaded || !placeSearchInput.trim()) return;
    e.preventDefault();
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: placeSearchInput.trim() }, (results, status) => {
      if (status !== 'OK' || !results?.[0]) return;
      applyLocationFromPlace(results[0]);
    });
  }, [googleMapsLoaded, placeSearchInput, applyLocationFromPlace]);

  useEffect(() => {
    if (currentStep !== 3 || !googleMapsLoaded || !autocompleteInputRef.current || autocompleteRef.current) return;

    const autocomplete = new window.google.maps.places.Autocomplete(autocompleteInputRef.current, {
      types: ['establishment', 'geocode'],
      componentRestrictions: { country: 'in' },
      fields: ['formatted_address', 'address_components', 'geometry', 'name', 'place_id'],
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place && place.place_id) applyLocationFromPlace(place);
    });

    autocompleteRef.current = autocomplete;

    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [currentStep, googleMapsLoaded, applyLocationFromPlace]);

  useEffect(() => {
    const inlineMap = inlineMapContainerRef.current;
    if (!inlineMap || !googleMapsLoaded) return;

    const defaultCenter =
      latitude && longitude
        ? { lat: Number(latitude), lng: Number(longitude) }
        : { lat: 20.5937, lng: 78.9629 };

    const map = new window.google.maps.Map(inlineMap, {
      center: defaultCenter,
      zoom: latitude && longitude ? 15 : 5,
      zoomControl: true,
      zoomControlOptions: { position: window.google.maps.ControlPosition.RIGHT_BOTTOM },
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    const marker = new window.google.maps.Marker({
      map,
      position: defaultCenter,
      draggable: true,
    });

    const geocoder = new window.google.maps.Geocoder();

    const updateMapSelection = (position) => {
      const latVal = position.lat();
      const lngVal = position.lng();
      setLatitude(String(latVal));
      setLongitude(String(lngVal));
      geocoder.geocode({ location: { lat: latVal, lng: lngVal } }, (results, status) => {
        if (status !== 'OK' || !results?.[0]) return;
        const components = extractAddressComponents(results[0].address_components || []);
        if (components.state) setState(components.state);
        if (components.country) setCountry(components.country);
      });
    };

    map.addListener('click', (event) => {
      marker.setPosition(event.latLng);
      updateMapSelection(event.latLng);
    });

    marker.addListener('dragend', (event) => {
      updateMapSelection(event.latLng);
    });

    return () => {
      if (marker) {
        window.google?.maps?.event?.clearInstanceListeners?.(marker);
      }
    };
  }, [inlineMapContainerRef, googleMapsLoaded, latitude, longitude, extractAddressComponents]);

  const handlePropertyTypeSelect = (typeId) => {
    setSelectedPropertyType(typeId);
  };

  const toggleSelection = (value, selectedList, setList) => {
    if (!value) return;
    setList((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
  };

  const parseDisplayTime = (timeValue) => {
    const [hoursRaw = '0', minutesRaw = '00'] = String(timeValue || '00:00').split(':');
    const h24 = Math.max(0, Math.min(23, Number(hoursRaw) || 0));
    const minutes = String(Math.max(0, Math.min(59, Number(minutesRaw) || 0))).padStart(2, '0');
    const isPM = h24 >= 12;
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return { h24, h12: String(h12), minutes, isPM };
  };

  const updateTimeFromParts = (which, nextHour12, nextMinutes, forceIsPM = null) => {
    const current = which === 'checkin' ? parseDisplayTime(checkinTime) : parseDisplayTime(checkoutTime);
    const safeHour12 = Math.max(1, Math.min(12, Number(nextHour12) || current.h12));
    const safeMin = String(Math.max(0, Math.min(59, Number(nextMinutes) || Number(current.minutes)))).padStart(2, '0');
    const isPM = forceIsPM == null ? current.isPM : forceIsPM;
    let h24 = safeHour12 % 12;
    if (isPM) h24 += 12;
    const composed = `${String(h24).padStart(2, '0')}:${safeMin}`;
    if (which === 'checkin') setCheckinTime(composed);
    else setCheckoutTime(composed);
  };

  const getStep1Errors = () => {
    const errors = {};
    if (!fullName.trim()) errors.fullName = 'Full name is required';
    if (!isValidEmail(accountEmail)) {
      errors.accountEmail = accountEmail.trim() ? 'Enter a valid email address' : 'Email is required';
    } else if (!skipOtp && !emailVerified) {
      errors.accountEmail = 'Email must be verified';
    }
    if (!isValidPhone(accountPhone)) errors.accountPhone = accountPhone.trim() ? 'Enter a valid 10-digit phone number' : 'Phone number is required';
    if (!password || password.length < 6) errors.password = 'Password must be at least 6 characters';
    if (password !== confirmPassword) errors.confirmPassword = 'Passwords do not match';
    return errors;
  };

  const getStep2Errors = () => {
    const errors = {};
    if (!propertyName.trim()) errors.propertyName = 'Property name is required';
    if (!selectedPropertyType) errors.selectedPropertyType = 'Please select a property type';
    return errors;
  };

  const getStep3Errors = () => {
    const errors = {};
    if (!address.trim()) errors.address = 'Street address is required';
    if (!city.trim()) errors.city = 'City is required';
    if (!state.trim()) errors.state = 'State is required';
    if (!country.trim()) errors.country = 'Country is required';
    return errors;
  };

  const getStep4Errors = () => {
    const errors = {};
    if (!propertyDescription.trim()) errors.propertyDescription = 'Property description is required';
    return errors;
  };

  const getStep5Errors = () => {
    const errors = {};
    if (!selectedAmenities.length) errors.amenities = 'Select at least one amenity';
    if (!selectedHotelPolicies.length) errors.policies = 'Select at least one hotel policy';
    if (!selectedCancellationPolicies.length) errors.cancellation = 'Select one cancellation policy';
    return errors;
  };

  const getKycErrors = () => {
    const errors = {};
    if (!agreedToPolicy) errors.agreedToPolicy = 'You must agree to the policy to continue';
    return errors;
  };

  const handleVerifyGst = async () => {
    const gstin = gstNumber.trim().toUpperCase();
    if (!gstin) {
      setGstError('Please enter a GST number first');
      return;
    }
    setGstVerifying(true);
    setGstError('');
    setGstVerifiedData(null);
    try {
      const result = await verifyGst(gstin);
      if (result.success && result.data) {
        if (result.data.status === 'Active') {
          setGstVerifiedData(result.data);
          if (result.data.pan) setPanNumber(result.data.pan);
          setGstLocked(true);
          setGstActive('active');
          successToast('GST verified successfully');
        } else {
          setGstError(`GST status is "${result.data.status}" — only Active GST numbers are accepted`);
        }
      } else {
        setGstError(result.message || 'Failed to verify GST number');
      }
    } catch (err) {
      setGstError(err?.response?.data?.message || 'Failed to verify GST number');
    } finally {
      setGstVerifying(false);
    }
  };

  const handleGstUnlock = () => {
    setGstLocked(false);
    setGstVerifiedData(null);
    setGstError('');
    setGstActive('');
  };

  const getStep7Errors = () => {
    const errors = {};
    const mainCount = (pendingImages.main_image || []).length;
    const propCount = (pendingImages.hotel_images || []).length;
    if (mainCount < MIN_MAIN_IMAGES) errors.main_image = `Add at least ${MIN_MAIN_IMAGES} main image`;
    if (propCount < MIN_PROPERTY_IMAGES) errors.hotel_images = `Add at least ${MIN_PROPERTY_IMAGES} property images`;
    return errors;
  };

  const roomsValidation = validateRooms(rooms);
  const step6LiveErrors = roomsValidation.perRoom[activeRoomIndex] || {};

  const refreshRateBlurWarnings = (rates = activeRoom.occupancyRates, occ = activeRoom.maxOccupancy) => {
    const mo = Math.max(1, Math.min(20, parseInt(occ, 10) || 1));
    const src = rates && typeof rates === 'object' ? rates : {};
    const warnings = {};
    for (let i = 1; i < mo; i += 1) {
      const a = Number(src[`${i}guest`]);
      const b = Number(src[`${i + 1}guest`]);
      if (Number.isFinite(a) && a > 0 && Number.isFinite(b) && b > 0 && a > b) {
        warnings[i] = `Must be ≤ ${i + 1}-guest price`;
        warnings[i + 1] = `Must be ≥ ${i}-guest price`;
      }
    }
    setRateBlurWarnings(warnings);
  };

  const isStep1Valid = Object.keys(getStep1Errors()).length === 0;
  const isStep2Valid = Object.keys(getStep2Errors()).length === 0;
  const isStep3Valid = Object.keys(getStep3Errors()).length === 0;
  const isStep4Valid = Object.keys(getStep4Errors()).length === 0;
  const isStep5Valid = Object.keys(getStep5Errors()).length === 0;
  const isStep6Valid = roomsValidation.ok;
  const isStep7Valid = Object.keys(getStep7Errors()).length === 0;
  const isKycValid = Object.keys(getKycErrors()).length === 0;

  const updateActiveRoom = (patch) => {
    setRooms((prev) =>
      prev.map((room, i) => {
        if (i !== activeRoomIndex) return room;
        return { ...room, ...(typeof patch === 'function' ? patch(room) : patch) };
      })
    );
  };

  const switchActiveRoom = (index) => {
    setActiveRoomIndex(index);
    setRateBlurWarnings({});
    setRoomSearchOpen(false);
    setRoomSearchResults([]);
  };

  const handleAddRoomType = () => {
    if (Object.keys(step6LiveErrors).length > 0) {
      setStep6Touched(true);
      refreshRateBlurWarnings();
      return;
    }
    setStep6Touched(false);
    setRooms((prev) => [...prev, createEmptyRoom()]);
    switchActiveRoom(rooms.length);
  };

  const handleRemoveRoomType = (index) => {
    if (rooms.length <= 1) return;
    setRooms((prev) => prev.filter((_, i) => i !== index));
    let next = activeRoomIndex;
    if (index < activeRoomIndex) next = activeRoomIndex - 1;
    else if (index === activeRoomIndex) next = Math.min(index, rooms.length - 2);
    switchActiveRoom(Math.max(0, next));
  };

  const handleNextStep = () => {
    if (currentStep < LAST_STEP) {
      setCurrentStep((s) => s + 1);
    }
  };

  const handleSendOtp = async (target) => {
    const value = target === 'email' ? accountEmail : accountPhone;
    if (!value) return;
    if (target === 'email' && !isValidEmail(accountEmail)) {
      errorToast('Please enter a valid email address');
      return;
    }
    if (target === 'phone' && !isValidPhone(accountPhone)) {
      errorToast('Please enter a valid phone number');
      return;
    }
    try {
      setOtpLoading(true);
      setOtpTarget(target);
      setOtpCode('');
      const identifier = target === 'email' ? accountEmail : accountPhone;
      const data = await sendSignupOtp({ identifier, type: target });
      if (data && data.status) {
        successToast(`OTP sent to ${target === 'email' ? 'email' : 'phone'}`);
        setOtpCooldown(60);
      } else {
        errorToast(data?.message || `Failed to send OTP to ${target === 'email' ? 'email' : 'phone'}`);
        setOtpTarget(null);
      }
    } catch (err) {
      errorToast(err?.response?.data?.message || `Failed to send OTP to ${target === 'email' ? 'email' : 'phone'}`);
      setOtpTarget(null);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length < 4) {
      errorToast('Please enter the OTP');
      return;
    }
    const identifier = otpTarget === 'email' ? accountEmail : accountPhone;
    if (!identifier) return;
    try {
      setOtpLoading(true);
      const data = await verifySignupOtp({ identifier, type: otpTarget, otp: otpCode });
      if (data && data.status) {
        successToast('Email verified successfully');
        setEmailVerified(true);
        draftStore.setItem('emailVerifiedSaved', 'true');
        draftStore.setItem('verifiedEmail', accountEmail);
        setOtpTarget(null);
        setOtpCode('');
        setOtpCooldown(0);
      } else {
        errorToast(data?.message || 'Invalid OTP');
      }
    } catch (err) {
      errorToast(err?.response?.data?.message || 'Failed to verify OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const advancePastAccountStep = () => {
    setCanSkipAccount(true);
    saveCurrentStep('signup', STEPS.TYPE);
    setStep1Touched(false);
    setCurrentStep(STEPS.TYPE);
  };

  const handleCreateAccount = async () => {
    if (!ensureVerification()) return;

    // Signup already succeeded for this verified email (e.g. reload / new tab): don't re-send it.
    if (isAccountCreatedFor(accountEmail)) {
      advancePastAccountStep();
      return;
    }

    try {
      setOtpLoading(true);
      const result = await signupHost({
        name: fullName,
        email: accountEmail,
        phone: accountPhone.replace(/\D/g, '').slice(-10),
        password,
        confirmPassword,
        gender: 'other',
      });
      if (result && result.status) {
        successToast('Account created successfully');
        if (result.token) {
          try {
            const encrypted = CryptoJS.AES.encrypt(result.token, 'tabtrips_hotel_secret').toString();
            Cookies.set('token', encrypted, { secure: true, sameSite: 'strict' });
          } catch {}
        }
        if (result.host_id) localStorage.setItem('selectedHostId', String(result.host_id));
        markAccountComplete(accountEmail, result.host_id);
        advancePastAccountStep();
      } else if (isAlreadyRegisteredMessage(result?.message)) {
        // The account exists and the user just proved they own the email via OTP.
        markAccountComplete(accountEmail);
        successToast('This email already has an account — continuing with your property details');
        advancePastAccountStep();
      } else {
        errorToast(result?.message || 'Failed to create account');
      }
    } catch (err) {
      const message = err?.response?.data?.message;
      if (isAlreadyRegisteredMessage(message)) {
        markAccountComplete(accountEmail);
        successToast('This email already has an account — continuing with your property details');
        advancePastAccountStep();
      } else {
        errorToast(message || 'Failed to create account');
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const minNavigableStep = isAddProperty || canSkipAccount ? STEPS.TYPE : STEPS.ACCOUNT;

  const handlePreviousStep = () => {
    if (currentStep > minNavigableStep) {
      setCurrentStep((s) => s - 1);
    }
  };

  const handleSelectStep = (stepId) => {
    if (submitting) return;
    if (stepId < minNavigableStep || stepId > maxReachedStep || stepId === currentStep) return;
    setCurrentStep(stepId);
  };

  const touchedSetter = {
    [STEPS.ACCOUNT]: setStep1Touched,
    [STEPS.TYPE]: setStep2Touched,
    [STEPS.LOCATION]: setStep3Touched,
    [STEPS.DETAILS]: setStep4Touched,
    [STEPS.AMENITIES]: setStep5Touched,
    [STEPS.IMAGES]: setStep7Touched,
    [STEPS.ROOMS]: setStep6Touched,
    [STEPS.KYC]: setKycTouched,
  };

  const continueButton = (stepNumber, label = 'Continue to Next Step') => {
    const validityMap = {
      [STEPS.ACCOUNT]: isStep1Valid,
      [STEPS.TYPE]: isStep2Valid,
      [STEPS.LOCATION]: isStep3Valid,
      [STEPS.DETAILS]: isStep4Valid,
      [STEPS.AMENITIES]: isStep5Valid,
      [STEPS.IMAGES]: isStep7Valid,
      [STEPS.ROOMS]: isStep6Valid,
      [STEPS.KYC]: isKycValid,
    };
    const isValid = validityMap[stepNumber];
    return (
      <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '40px' }}>
        <button
          onClick={handlePreviousStep}
          style={{
            backgroundColor: 'transparent',
            color: '#64748b',
            border: '2px solid #e2e8f0',
            padding: '14px 32px',
            fontSize: '1rem',
            fontWeight: '600',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => { e.target.style.backgroundColor = '#f8fafc'; e.target.style.borderColor = '#cbd5e1'; }}
          onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = '#e2e8f0'; }}
        >
          Back
        </button>
        <button
          onClick={() => {
            if (!isValid) {
              const setter = touchedSetter[stepNumber];
              if (setter) setter(true);
              if (stepNumber === STEPS.ROOMS) {
                if (roomsValidation.firstInvalidIndex >= 0) switchActiveRoom(roomsValidation.firstInvalidIndex);
                refreshRateBlurWarnings();
              }
              return;
            }
            const setter = touchedSetter[stepNumber];
            if (setter) setter(false);
            if (stepNumber === STEPS.ROOMS) setRateBlurWarnings({});
            handleNextStep();
          }}
          style={{
            background: isValid ? 'linear-gradient(135deg, #FF7700, #FF8800)' : '#cbd5e1',
            color: '#000',
            border: 'none',
            padding: '14px 32px',
            fontSize: '1rem',
            fontWeight: '600',
            borderRadius: '10px',
            boxShadow: isValid ? '0 4px 12px rgba(255,119,0,0.3)' : 'none',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            if (isValid) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(255,119,0,0.4)';
            }
          }}
          onMouseLeave={(e) => {
            if (isValid) {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(255,119,0,0.3)';
            }
          }}
        >
          {label}
        </button>
      </div>
    );
  };

  const renderField = (key, label, value, onChange, type = 'text', icon = null, error = null, placeholder = '') => (
    <div key={key} style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label} {error && <span style={{ color: '#dc2626' }}>*</span>}
      </label>
      <div style={{ position: 'relative' }}>
        {icon && (
          <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
            <FontAwesomeIcon icon={icon} size="sm" />
          </div>
        )}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: icon ? '16px 16px 16px 48px' : '16px',
            fontSize: '1rem',
            border: `2px solid ${error ? '#dc2626' : '#e2e8f0'}`,
            borderRadius: '12px',
            outline: 'none',
            transition: 'all 0.3s ease',
            backgroundColor: '#f8fafc'
          }}
          onFocus={(e) => { e.target.style.borderColor = '#FF7700'; e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)'; }}
          onBlur={(e) => { e.target.style.borderColor = error ? '#dc2626' : '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
        />
      </div>
      {error && <span style={{ display: 'block', color: '#dc2626', fontSize: '0.8rem', marginTop: '6px' }}>{error}</span>}
    </div>
  );

  const renderVerifyField = (key, label, value, onChange, type = 'text', icon = null, error = null, placeholder = '', target = 'email', verified = false) => (
    <div key={key} style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label} {error && <span style={{ color: '#dc2626' }}>*</span>}
        {verified && <span style={{ color: '#16a34a', fontSize: '0.75rem', marginLeft: '8px', fontWeight: 700 }}>✓ Verified</span>}
      </label>
      <div style={{ position: 'relative', display: 'flex', gap: '10px' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          {icon && (
            <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
              <FontAwesomeIcon icon={icon} size="sm" />
            </div>
          )}
          <input
            type={type}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              if (target === 'email' && emailVerified) {
                setEmailVerified(false);
                draftStore.removeItem('emailVerifiedSaved');
                draftStore.removeItem('verifiedEmail');
              }
            }}
            placeholder={placeholder}
            disabled={verified}
            style={{
              width: '100%',
              padding: icon ? '16px 16px 16px 48px' : '16px',
              fontSize: '1rem',
              border: `2px solid ${error ? '#dc2626' : verified ? '#16a34a' : '#e2e8f0'}`,
              borderRadius: '12px',
              outline: 'none',
              transition: 'all 0.3s ease',
              backgroundColor: verified ? '#f0fdf4' : '#f8fafc'
            }}
            onFocus={(e) => { e.target.style.borderColor = '#FF7700'; e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)'; }}
            onBlur={(e) => { e.target.style.borderColor = error ? '#dc2626' : verified ? '#16a34a' : '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
          />
        </div>
        {!verified && (
          <button
            type="button"
            onClick={() => handleSendOtp(target)}
            disabled={otpLoading || (otpCooldown > 0 && otpTarget === target)}
            style={{
              whiteSpace: 'nowrap',
              padding: '0 20px',
              fontSize: '0.9rem',
              fontWeight: 600,
              borderRadius: '10px',
              border: 'none',
              cursor: otpLoading || (otpCooldown > 0 && otpTarget === target) ? 'not-allowed' : 'pointer',
              background: otpCooldown > 0 && otpTarget === target ? '#cbd5e1' : 'linear-gradient(135deg, #FF7700, #FF8800)',
              color: '#000'
            }}
          >
            {otpTarget === target && otpCooldown > 0 ? `Resend (${otpCooldown})` : otpTarget === target && otpLoading ? 'Sending...' : 'Verify'}
          </button>
        )}
      </div>
      {error && <span style={{ display: 'block', color: '#dc2626', fontSize: '0.8rem', marginTop: '6px' }}>{error}</span>}

      {otpTarget === target && !verified && (
        <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
          <input
            type="text"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder={`Enter OTP sent to ${target === 'email' ? 'email' : 'phone'}`}
            maxLength={6}
            style={{
              flex: 1,
              padding: '14px 16px',
              fontSize: '1rem',
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              outline: 'none',
              backgroundColor: '#fff',
              textAlign: 'center',
              letterSpacing: '4px'
            }}
          />
          <button
            type="button"
            onClick={handleVerifyOtp}
            disabled={otpLoading || otpCode.length < 4}
            style={{
              whiteSpace: 'nowrap',
              padding: '0 22px',
              fontSize: '0.95rem',
              fontWeight: 600,
              borderRadius: '10px',
              border: 'none',
              cursor: otpLoading || otpCode.length < 4 ? 'not-allowed' : 'pointer',
              background: otpCode.length >= 4 ? 'linear-gradient(135deg, #FF7700, #FF8800)' : '#cbd5e1',
              color: '#000'
            }}
          >
            {otpLoading ? 'Verifying...' : 'Confirm OTP'}
          </button>
        </div>
      )}
    </div>
  );

  const renderCheckboxGrid = (options, selected, setSelected, error) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px', maxHeight: '320px', overflowY: 'auto', padding: '8px', border: `1px solid ${error ? '#dc2626' : '#e2e8f0'}`, borderRadius: '12px', backgroundColor: '#fff' }}>
      {options.map((item) => (
        <label key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px', borderRadius: '10px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={selected.includes(item)}
            onChange={() => toggleSelection(item, selected, setSelected)}
            style={{ width: '18px', height: '18px', marginTop: '2px', accentColor: '#FF7700', flexShrink: 0 }}
          />
          <span style={{ color: '#334155', fontSize: '0.9rem', lineHeight: '1.3' }}>{item}</span>
        </label>
      ))}
    </div>
  );

  const clearDrafts = () => {
    clearListingDraft();
    window.dispatchEvent(new Event('listingDraftChanged'));
  };

  // --- Step 7 images (in-memory only; File objects can't be serialised to the draft)
  useEffect(() => {
    pendingImagesRef.current = pendingImages;
  }, [pendingImages]);

  useEffect(() => {
    roomImagesRef.current = roomImages;
  }, [roomImages]);

  useEffect(() => () => {
    Object.values(pendingImagesRef.current).flat().forEach((img) => URL.revokeObjectURL(img.previewUrl));
    Object.values(roomImagesRef.current).flat().forEach((img) => URL.revokeObjectURL(img.previewUrl));
  }, []);

  const addImagesToSection = (sectionKey, fileList) => {
    const section = HOTEL_IMAGE_SECTIONS.find((sec) => sec.key === sectionKey);
    if (!section) return;
    const files = Array.from(fileList || []).filter((file) => file && file.type.startsWith('image/'));
    if (files.length === 0) {
      errorToast('Please choose image files only (JPG, PNG, WEBP).');
      return;
    }
    setPendingImages((prev) => {
      const existing = prev[sectionKey] || [];
      if (!section.multiple) {
        existing.forEach((img) => URL.revokeObjectURL(img.previewUrl));
        return { ...prev, [sectionKey]: [makeImageEntry(files[0])] };
      }
      const seen = new Set(existing.map((img) => `${img.name}:${img.size}`));
      const fresh = files.filter((file) => !seen.has(`${file.name}:${file.size}`)).map(makeImageEntry);
      return { ...prev, [sectionKey]: [...existing, ...fresh] };
    });
  };

  const removeImage = (sectionKey, imageId) => {
    setPendingImages((prev) => {
      const list = prev[sectionKey] || [];
      const target = list.find((img) => img.id === imageId);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return { ...prev, [sectionKey]: list.filter((img) => img.id !== imageId) };
    });
  };

  const addRoomImages = (roomId, fileList) => {
    const files = Array.from(fileList || []).filter((file) => file && file.type.startsWith('image/'));
    if (files.length === 0) {
      errorToast('Please choose image files only (JPG, PNG, WEBP).');
      return;
    }
    setRoomImages((prev) => {
      const existing = prev[roomId] || [];
      const seen = new Set(existing.map((img) => `${img.name}:${img.size}`));
      const fresh = files.filter((file) => !seen.has(`${file.name}:${file.size}`)).map(makeImageEntry);
      return { ...prev, [roomId]: [...existing, ...fresh] };
    });
  };

  const removeRoomImage = (roomId, imageId) => {
    setRoomImages((prev) => {
      const list = prev[roomId] || [];
      const target = list.find((img) => img.id === imageId);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return { ...prev, [roomId]: list.filter((img) => img.id !== imageId) };
    });
  };

  const openRoomImagePicker = (roomId) => {
    roomImageInputRefs.current[roomId]?.click();
  };

  const openImagePicker = (sectionKey) => {
    imageInputRefs.current[sectionKey]?.click();
  };

  const totalPendingImages = countImages(pendingImages);
  const imageCounts = Object.fromEntries(
    HOTEL_IMAGE_SECTIONS.map((section) => [section.key, (pendingImages[section.key] || []).length])
  );
  const kycImageCounts = Object.fromEntries(
    KYC_IMAGE_SECTIONS.map((section) => [section.key, (pendingImages[section.key] || []).length])
  );

  const wizardState = {
    fullName, accountEmail, accountPhone, password, confirmPassword,
    propertyName, selectedPropertyType, address, city, state, country, pincode, latitude, longitude,
    propertyDescription, phoneNumber, email, checkinTime, checkoutTime,
    selectedAmenities, selectedHotelPolicies, selectedCancellationPolicies, isOpen24x7, isCoupleFriendly,
    panNumber, gstNumber, gstActive, agreedToPolicy,
    rooms,
    imageCounts,
    kycImageCounts,
  };

  const handleCompleteListing = async () => {
    if (!isStep7Valid) {
      setStep7Touched(true);
      setCurrentStep(STEPS.IMAGES);
      return;
    }
    if (!roomsValidation.ok) {
      setStep6Touched(true);
      if (roomsValidation.firstInvalidIndex >= 0) switchActiveRoom(roomsValidation.firstInvalidIndex);
      setCurrentStep(STEPS.ROOMS);
      return;
    }
    if (!isKycValid) {
      setKycTouched(true);
      setCurrentStep(STEPS.KYC);
      return;
    }
    startSubmit();
    setImageUploadProgress(0);
    const payload = buildPropertyPayload(wizardState);
    const authToken = getDecryptedToken();
    const imagesBySection = Object.fromEntries(
      Object.entries(pendingImages).map(([key, list]) => [key, list.map((img) => img.file)])
    );
    rooms.forEach((room) => {
      const imgs = roomImages[room.id] || [];
      if (imgs.length > 0 && room.roomTypeSafeName) {
        imagesBySection[room.roomTypeSafeName] = imgs.map((img) => img.file);
      }
    });
    const result = await submitOnboarding({
      payload,
      rooms,
      imagesBySection,
      onImageProgress: setImageUploadProgress,
      refetch,
      successToast,
      errorToast,
      handleInvalidToken,
      mode,
      authToken,
      existingHotelId: pendingHotelId,
    });
    if (result.ok) {
      setPendingHotelId(null);
      const summary = buildOnboardingSummary({
        ...wizardState,
        imageCounts: result.imagesUploaded ? imageCounts : {},
      });
      setCreatedHotel({ hotelId: result.hotel_id, hotelName: propertyName, summary });
      setSuccess();
      clearDrafts();
    } else {
      if (result.hotel_id && result.createdWithoutImages) {
        setPendingHotelId(String(result.hotel_id));
      }
      setSubmitError(result.message || 'Failed to create property');
    }
  };

  if (success && createdHotel) {
    return (
      <OnboardingSuccess
        hotelName={createdHotel.hotelName}
        hotelId={createdHotel.hotelId}
        summary={createdHotel.summary}
        onContinue={() => {
          reset();
          const hasToken = Cookies.get('token');
          navigate(isAddProperty || hasToken ? '/dashboard' : '/login');
        }}
        footer={<ListingSupportBox style={{ marginTop: 24, textAlign: 'left' }} />}
      />
    );
  }

  const step1Errors = getStep1Errors();
  const step2Errors = getStep2Errors();
  const step3Errors = getStep3Errors();
  const step4Errors = getStep4Errors();
  const step5Errors = getStep5Errors();
  const step7Errors = getStep7Errors();
  const kycErrors = getKycErrors();

  const filteredCancellationOptions = [
    cancellationPoliciesOptions[0],
    cancellationPoliciesOptions[1],
    cancellationPoliciesOptions[cancellationPoliciesOptions.length - 1],
  ].filter(Boolean);

  const wizardSteps = getWizardSteps(mode);
  const stepOrdinal = getStepOrdinal(mode, currentStep);

  return (
    <div className="listing-shell" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #FFE6CC 0%, #FFCC99 100%)', padding: '20px', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', position: 'relative', overflow: 'hidden' }}>
      <div className="listing-floating-shapes" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', width: 90, height: 90, borderRadius: '50%', background: 'rgba(255,255,255,0.4)', boxShadow: '0 4px 20px rgba(255,255,255,0.3)', top: '12%', left: '8%', animation: 'listing-float 7s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.35)', boxShadow: '0 4px 20px rgba(255,255,255,0.3)', top: '55%', right: '12%', animation: 'listing-float 9s ease-in-out infinite 2s' }} />
        <div style={{ position: 'absolute', width: 70, height: 70, borderRadius: '50%', background: 'rgba(255,255,255,0.4)', boxShadow: '0 4px 20px rgba(255,255,255,0.3)', bottom: '15%', left: '18%', animation: 'listing-float 8s ease-in-out infinite 4s' }} />
        <div style={{ position: 'absolute', width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.3)', boxShadow: '0 4px 20px rgba(255,255,255,0.3)', top: '28%', right: '28%', animation: 'listing-float 10s ease-in-out infinite 1s' }} />
      </div>

      <div className="listing-content" style={{ position: 'relative', zIndex: 2, maxWidth: '1180px', margin: '0 auto' }}>
        <ListingHeader mode={mode} />

        <div style={{ background: '#fff', borderRadius: 20, padding: '32px', boxShadow: '0 20px 60px rgba(255,119,0,0.12), 0 4px 12px rgba(0,0,0,0.04)', border: '1px solid rgba(255,119,0,0.10)', marginBottom: 24 }}>

          {showResumeBanner && currentStep > firstStep && (
            <div style={{ marginBottom: 24, padding: '14px 18px', borderRadius: 12, background: '#fff7ed', border: '1px solid #fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ color: '#9a3412', fontWeight: 600, fontSize: '0.95rem' }}>
                Welcome back — you&apos;re on step {currentStep - firstStep + 1} of {totalSteps}. Your saved details are restored.
              </span>
              <button
                type="button"
                onClick={() => setShowResumeBanner(false)}
                style={{ border: 'none', background: 'transparent', color: '#c2410c', fontWeight: 700, cursor: 'pointer' }}
              >
                Dismiss
              </button>
            </div>
          )}

          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <h1 style={{ fontSize: '2.5rem', fontWeight: '800', color: '#333', marginBottom: '10px', letterSpacing: '-1px' }}>
              {isAddProperty ? (
                <>Add a <span style={{ color: '#FF7700', position: 'relative' }}>
                  Property
                  <span style={{ position: 'absolute', bottom: -2, left: 0, width: '100%', height: '3px', background: 'linear-gradient(135deg, #FF7700, #FFB366)', borderRadius: '2px' }} />
                </span></>
              ) : (
                <>List Your <span style={{ color: '#FF7700', position: 'relative' }}>
                  Property
                  <span style={{ position: 'absolute', bottom: -2, left: 0, width: '100%', height: '3px', background: 'linear-gradient(135deg, #FF7700, #FFB366)', borderRadius: '2px' }} />
                </span></>
              )}
            </h1>
            <p style={{ fontSize: '1.1rem', color: '#555', maxWidth: '600px', margin: '0 auto' }}>
              {isAddProperty
                ? 'Register another property on your TabTrips account.'
                : "Let's get started with the basics. Tell us about your property."}
            </p>
          </div>

          <div className="listing-wizard">
            <ListingStepper
              steps={wizardSteps}
              currentStep={currentStep}
              maxReachedStep={maxReachedStep}
              minStep={minNavigableStep}
              onSelect={handleSelectStep}
              disabled={submitting}
            />
            <div className="listing-wizard-main">
              <p className="listing-step-kicker">
                Step {stepOrdinal.current}/{stepOrdinal.total}
              </p>

          {/* Step 1: Account Setup (signup only) */}
          {currentStep === 1 && !isAddProperty && (
            <div style={{ maxWidth: '700px', margin: '0 auto' }}>
              <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#333', textAlign: 'center', marginBottom: '40px' }}>
                Setup Your Account
              </h2>
              <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '32px', border: '2px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                {skipOtp && (
                  <p style={{ margin: '0 0 20px', color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 14px', fontSize: '0.95rem', lineHeight: 1.45 }}>
                    No email OTP is sent. The address you enter is verified when the account is created.
                  </p>
                )}
                {renderField('fullName', 'Full Name', fullName, setFullName, 'text', faUser, step1Touched && step1Errors.fullName, 'Enter your full name')}
                {skipOtp
                  ? renderField('accountEmail', 'Email Address', accountEmail, setAccountEmail, 'email', faEnvelope, step1Touched && step1Errors.accountEmail, 'Enter the host email')
                  : renderVerifyField('accountEmail', 'Email Address', accountEmail, setAccountEmail, 'email', faEnvelope, step1Touched && step1Errors.accountEmail, 'Enter your email', 'email', emailVerified)}
                {renderField('accountPhone', 'Phone Number', accountPhone, setAccountPhone, 'tel', faPhone, step1Touched && step1Errors.accountPhone, '10-digit mobile number')}
                {renderField('password', 'Password', password, setPassword, 'password', faLock, step1Touched && step1Errors.password, 'Min 6 characters')}
                {renderField('confirmPassword', 'Confirm Password', confirmPassword, setConfirmPassword, 'password', faLock, step1Touched && step1Errors.confirmPassword, 'Re-enter password')}
              </div>
              {step1Touched && Object.keys(step1Errors).length > 0 && (
                <div style={{ textAlign: 'center', marginTop: '16px', color: '#dc2626', fontSize: '0.9rem', fontWeight: 600 }}>
                  Please complete the {Object.keys(step1Errors).length} highlighted field{Object.keys(step1Errors).length > 1 ? 's' : ''} above to continue.
                </div>
              )}
              <div style={{ textAlign: 'center', marginTop: '40px' }}>
                <button
                  onClick={() => {
                    if (!isStep1Valid) { setStep1Touched(true); return; }
                    handleCreateAccount();
                  }}
                  disabled={otpLoading}
                  style={{
                    background: isStep1Valid && !otpLoading ? 'linear-gradient(135deg, #FF7700, #FF8800)' : '#cbd5e1',
                    color: '#000',
                    border: 'none',
                    padding: '16px 40px',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    borderRadius: '12px',
                    boxShadow: isStep1Valid && !otpLoading ? '0 4px 12px rgba(255,119,0,0.3)' : 'none',
                    cursor: otpLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => { if (isStep1Valid && !otpLoading) { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 6px 20px rgba(255,119,0,0.4)'; }}}
                  onMouseLeave={(e) => { if (isStep1Valid && !otpLoading) { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 12px rgba(255,119,0,0.3)'; }}}
                >
                  {otpLoading ? 'Processing...' : 'Continue to Property Type'}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Property Type */}
          {currentStep === 2 && (
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
              <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#333', textAlign: 'center', marginBottom: '40px' }}>
                What type of property is this?
              </h2>
              <div style={{ marginBottom: '40px' }}>
                <label style={{ display: 'block', fontSize: '1.25rem', fontWeight: '600', color: '#1e293b', marginBottom: '20px', textAlign: 'center' }}>
                  Property Name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                  <input
                    type="text"
                    value={propertyName}
                    onChange={(e) => setPropertyName(e.target.value)}
                    placeholder="Enter your property name..."
                    style={{
                      width: '100%',
                      maxWidth: '500px',
                      padding: '16px 20px',
                      fontSize: '1.1rem',
                      border: `2px solid ${step2Touched && step2Errors.propertyName ? '#dc2626' : '#e2e8f0'}`,
                      borderRadius: '12px',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      backgroundColor: 'white'
                    }}
                    onFocus={(e) => { e.target.style.borderColor = '#FF7700'; e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                  />
                  {step2Touched && step2Errors.propertyName && <span style={{ display: 'block', color: '#dc2626', fontSize: '0.85rem', marginTop: '8px' }}>{step2Errors.propertyName}</span>}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', padding: '16px' }}>
                {propertyTypes.map((type) => {
                  const IconComponent = type.icon;
                  const isSelected = selectedPropertyType === type.id;
                  return (
                    <div
                      key={type.id}
                      onClick={() => handlePropertyTypeSelect(type.id)}
                      style={{
                        background: isSelected ? 'linear-gradient(135deg, rgba(255,119,0,0.06), rgba(253,185,19,0.06))' : 'white',
                        border: `2px solid ${isSelected ? '#FF7700' : '#e2e8f0'}`,
                        borderRadius: '14px',
                        padding: '20px 16px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.25s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: isSelected ? '0 0 0 3px rgba(255,119,0,0.12)' : 'none'
                      }}
                      onMouseEnter={(e) => { if (!isSelected) { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(255,119,0,0.12)'; e.currentTarget.style.borderColor = '#FF7700'; }}}
                      onMouseLeave={(e) => { if (!isSelected) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e2e8f0'; }}}
                    >
                      {isSelected && (
                        <div style={{ position: 'absolute', top: '15px', right: '15px', width: '24px', height: '24px', backgroundColor: '#FF7700', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px', fontWeight: 'bold' }}>
                          ✓
                        </div>
                      )}
                      <div style={{ marginBottom: '16px' }}>
                        <FontAwesomeIcon icon={IconComponent} size="4x" color={isSelected ? '#FF7700' : '#64748b'} />
                      </div>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: isSelected ? '#FF7700' : '#1e293b', marginBottom: '8px' }}>{type.name}</h3>
                      <p style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: '1.3' }}>{type.description}</p>
                    </div>
                  );
                })}
              </div>
              {step2Touched && step2Errors.selectedPropertyType && <span style={{ display: 'block', textAlign: 'center', color: '#dc2626', fontSize: '0.85rem', marginTop: '8px' }}>{step2Errors.selectedPropertyType}</span>}
              {step2Touched && Object.keys(step2Errors).length > 0 && (
                <div style={{ textAlign: 'center', marginTop: '16px', color: '#dc2626', fontSize: '0.9rem', fontWeight: 600 }}>
                  Please complete the {Object.keys(step2Errors).length} highlighted field{Object.keys(step2Errors).length > 1 ? 's' : ''} above to continue.
                </div>
              )}
              {continueButton(2, 'Continue to Location')}
            </div>
          )}

          {/* Step 3: Location */}
          {currentStep === 3 && (
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
              <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#333', textAlign: 'center', marginBottom: '40px' }}>
                Where is your property located?
              </h2>

              <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 320px' }}>
                  <label style={{ fontSize: '0.9rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', display: 'block' }}>
                    Search Location
                  </label>
                  <input
                    ref={autocompleteInputRef}
                    type="text"
                    value={placeSearchInput}
                    onChange={(e) => setPlaceSearchInput(e.target.value)}
                    onKeyDown={handleLocationSearchSubmit}
                    placeholder="Search property/location name..."
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      fontSize: '1rem',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      outline: 'none',
                      backgroundColor: '#f8fafc',
                      marginBottom: '24px'
                    }}
                  />

                  <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', border: '2px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
                      {renderField('address', 'Street Address *', address, setAddress, 'text', faHome, step3Touched && step3Errors.address, 'Enter street address')}
                      {renderField('city', 'City *', city, setCity, 'text', faBuilding, step3Touched && step3Errors.city, 'Enter city')}
                      {renderField('pincode', 'Pincode', pincode, setPincode, 'text', null, null, 'Enter pincode')}
                      {renderField('state', 'State/Province *', state, setState, 'text', faUmbrellaBeach, step3Touched && step3Errors.state, 'Enter state or province')}
                      {renderField('country', 'Country *', country, setCountry, 'text', faCampground, step3Touched && step3Errors.country, 'Enter country')}
                      {renderField('latitude', 'Latitude', latitude, setLatitude, 'text', null, null, 'Latitude')}
                      {renderField('longitude', 'Longitude', longitude, setLongitude, 'text', null, null, 'Longitude')}
                    </div>
                  </div>
                </div>

                <div style={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <label style={{ fontSize: '0.9rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', display: 'block' }}>
                    Pin on Map
                  </label>
                  <div ref={inlineMapContainerRef} style={{ width: '100%', height: '420px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '2px solid #e2e8f0' }} />
                  <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '8px', textAlign: 'center' }}>
                    Click or drag the marker to set latitude, longitude, state and country.
                  </p>
                </div>
              </div>

              {step3Touched && Object.keys(step3Errors).length > 0 && (
                <div style={{ textAlign: 'center', marginTop: '16px', color: '#dc2626', fontSize: '0.9rem', fontWeight: 600 }}>
                  Please complete the {Object.keys(step3Errors).length} highlighted field{Object.keys(step3Errors).length > 1 ? 's' : ''} above to continue.
                </div>
              )}
              {continueButton(3, 'Continue to Details')}
            </div>
          )}

          {/* Step 4: Description */}
          {currentStep === 4 && (
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
              <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#333', textAlign: 'center', marginBottom: '40px' }}>
                Tell us more about your property
              </h2>

              <div style={{ display: 'grid', gap: '32px', marginBottom: '40px' }}>
                <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', border: '2px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                    <div style={{ width: '40px', height: '40px', backgroundColor: '#fef3c7', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '16px' }}>
                      <FontAwesomeIcon icon={faBuilding} size="lg" color="#f59e0b" />
                    </div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e293b', margin: 0 }}>Property Description</h3>
                  </div>
                  <textarea
                    value={propertyDescription}
                    onChange={(e) => setPropertyDescription(e.target.value)}
                    placeholder="Describe your property, amenities, unique features, what makes it special..."
                    rows="4"
                    style={{
                      width: '100%',
                      padding: '16px',
                      fontSize: '1rem',
                      border: `2px solid ${step4Touched && step4Errors.propertyDescription ? '#dc2626' : '#e2e8f0'}`,
                      borderRadius: '12px',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      backgroundColor: '#f8fafc',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      lineHeight: '1.5'
                    }}
                    onFocus={(e) => { e.target.style.borderColor = '#FF7700'; e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)'; e.target.style.backgroundColor = 'white'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; e.target.style.backgroundColor = '#f8fafc'; }}
                  />
                  {step4Touched && step4Errors.propertyDescription && <span style={{ display: 'block', color: '#dc2626', fontSize: '0.8rem', marginTop: '6px' }}>{step4Errors.propertyDescription}</span>}
                  <div style={{ marginTop: '10px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const template = buildDescriptionTemplate();
                        if (template) setPropertyDescription(template);
                        else errorToast('Enter the property location first (city/state) to generate a description.');
                      }}
                      style={{ padding: '8px 16px', fontSize: '0.85rem', fontWeight: 600, borderRadius: '8px', border: '1.5px solid #FF7700', background: '#fff7ed', color: '#c2410c', cursor: 'pointer' }}
                    >
                      <FontAwesomeIcon icon={faPen} size="sm" style={{ marginRight: 6 }} />
                      Generate from location
                    </button>
                  </div>
                </div>

                <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', border: '2px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                    <div style={{ width: '40px', height: '40px', backgroundColor: '#dcfce7', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '16px' }}>
                      <FontAwesomeIcon icon={faUmbrellaBeach} size="lg" color="#16a34a" />
                    </div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e293b', margin: 0 }}>Check-in & Check-out</h3>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                    {['checkin', 'checkout'].map((which) => {
                      const current = which === 'checkin' ? parseDisplayTime(checkinTime) : parseDisplayTime(checkoutTime);
                      return (
                        <div key={which}>
                          <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', color: '#64748b', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>
                            {which === 'checkin' ? 'Check-in Time' : 'Check-out Time'}
                          </label>
                          <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '2px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                              <input
                                type="text"
                                value={current.h12}
                                maxLength={2}
                                onChange={(e) => {
                                  const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, 2);
                                  if (!onlyDigits) return updateTimeFromParts(which, 12, current.minutes);
                                  updateTimeFromParts(which, onlyDigits, current.minutes);
                                }}
                                style={{ width: '56px', textAlign: 'center', padding: '10px 8px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1.45rem', fontWeight: '600', color: '#1e293b', lineHeight: 1 }}
                              />
                              <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b' }}>:</span>
                              <input
                                type="text"
                                value={current.minutes}
                                maxLength={2}
                                onChange={(e) => {
                                  const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, 2);
                                  if (!onlyDigits) return updateTimeFromParts(which, current.h12, '00');
                                  updateTimeFromParts(which, current.h12, onlyDigits);
                                }}
                                style={{ width: '56px', textAlign: 'center', padding: '10px 8px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1.45rem', fontWeight: '600', color: '#1e293b', lineHeight: 1 }}
                              />
                              <button
                                onClick={() => updateTimeFromParts(which, current.h12, current.minutes, false)}
                                style={{ padding: '8px 16px', fontSize: '13px', fontWeight: '600', border: '2px solid #e2e8f0', borderRadius: '20px', cursor: 'pointer', backgroundColor: current.isPM ? 'white' : '#FF7700', color: current.isPM ? '#64748b' : 'white', transition: 'all 0.3s ease', minWidth: '60px' }}
                              >
                                AM
                              </button>
                              <button
                                onClick={() => updateTimeFromParts(which, current.h12, current.minutes, true)}
                                style={{ padding: '8px 16px', fontSize: '13px', fontWeight: '600', border: '2px solid #e2e8f0', borderRadius: '20px', cursor: 'pointer', backgroundColor: current.isPM ? '#FF7700' : 'white', color: current.isPM ? 'white' : '#64748b', transition: 'all 0.3s ease', minWidth: '60px' }}
                              >
                                PM
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {step4Touched && Object.keys(step4Errors).length > 0 && (
                <div style={{ textAlign: 'center', marginBottom: '16px', color: '#dc2626', fontSize: '0.9rem', fontWeight: 600 }}>
                  Please complete the {Object.keys(step4Errors).length} highlighted field{Object.keys(step4Errors).length > 1 ? 's' : ''} above to continue.
                </div>
              )}
              {continueButton(4, 'Continue to Amenities & Policies')}
            </div>
          )}

          {/* Step 5: Amenities & Policies */}
          {currentStep === 5 && (
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
              <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#333', textAlign: 'center', marginBottom: '40px' }}>
                Amenities & Policies
              </h2>
              <div style={{ display: 'grid', gap: '32px', marginBottom: '40px' }}>
                <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', border: '2px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                  <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                    Amenities <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  {renderCheckboxGrid(amenitiesOptions, selectedAmenities, setSelectedAmenities, step5Touched && step5Errors.amenities)}
                  {step5Touched && step5Errors.amenities && <span style={{ display: 'block', color: '#dc2626', fontSize: '0.85rem', marginTop: '8px' }}>{step5Errors.amenities}</span>}
                </div>

                <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', border: '2px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                  <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                    Hotel Policies <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  {renderCheckboxGrid(hotelPoliciesOptions, selectedHotelPolicies, setSelectedHotelPolicies, step5Touched && step5Errors.policies)}
                  {step5Touched && step5Errors.policies && <span style={{ display: 'block', color: '#dc2626', fontSize: '0.85rem', marginTop: '8px' }}>{step5Errors.policies}</span>}
                </div>

                <div style={{ backgroundColor: '#f0fdf4', borderRadius: '16px', padding: '24px', border: '2px solid #bbf7d0', boxShadow: '0 2px 8px rgba(22, 163, 74, 0.08)' }}>
                  <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '600', color: '#14532d', marginBottom: '8px' }}>
                    Cancellation Policies <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <p style={{ fontSize: '0.85rem', color: '#16a34a', marginBottom: '16px', marginTop: 0 }}>Select one cancellation policy. The recommended option is highlighted in green.</p>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {filteredCancellationOptions.map((item) => {
                      const isChecked = selectedCancellationPolicies.includes(item);
                      const isRecommended = item === cancellationPoliciesOptions[0];
                      return (
                        <label key={item} style={{
                          display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 16px', borderRadius: '12px',
                          backgroundColor: isRecommended ? '#dcfce7' : isChecked ? '#bbf7d0' : '#ffffff',
                          border: `2px solid ${isChecked ? '#16a34a' : isRecommended ? '#86efac' : '#e2e8f0'}`,
                          cursor: 'pointer', transition: 'all 0.2s ease',
                        }}>
                          <input type="radio" name="cancellationPolicy" checked={isChecked}
                            onChange={() => setSelectedCancellationPolicies([item])}
                            style={{ width: '18px', height: '18px', marginTop: '2px', accentColor: '#16a34a', flexShrink: 0 }} />
                          <div>
                            <span style={{ color: '#14532d', fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.3, display: 'block' }}>{item}</span>
                            {isRecommended && <span style={{ display: 'inline-block', marginTop: 6, fontSize: '0.7rem', fontWeight: 700, color: '#fff', background: '#16a34a', padding: '2px 10px', borderRadius: 10 }}>Recommended</span>}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {step5Touched && step5Errors.cancellation && <span style={{ display: 'block', color: '#dc2626', fontSize: '0.85rem', marginTop: '10px' }}>{step5Errors.cancellation}</span>}
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 700, color: '#334155', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px 14px', cursor: 'pointer' }}>
                    <input type="checkbox" style={{ width: 18, height: 18 }} checked={isOpen24x7} onChange={(e) => setIsOpen24x7(e.target.checked)} />
                    Open 24/7
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 700, color: '#334155', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px 14px', cursor: 'pointer' }}>
                    <input type="checkbox" style={{ width: 18, height: 18 }} checked={isCoupleFriendly} onChange={(e) => setIsCoupleFriendly(e.target.checked)} />
                    Couple Friendly
                  </label>
                </div>
              </div>

              {step5Touched && Object.keys(step5Errors).length > 0 && (
                <div style={{ textAlign: 'center', marginBottom: '16px', color: '#dc2626', fontSize: '0.9rem', fontWeight: 600 }}>
                  Please complete the {Object.keys(step5Errors).length} highlighted field{Object.keys(step5Errors).length > 1 ? 's' : ''} above to continue.
                </div>
              )}
              {continueButton(5, 'Continue to Property Images')}
            </div>
          )}

          {/* Step 6: Room Setup — one or more room types */}
          {currentStep === STEPS.ROOMS && (
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
              <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#333', textAlign: 'center', marginBottom: '12px' }}>
                Room Setup
              </h2>
              <p style={{ textAlign: 'center', color: '#64748b', fontSize: '1rem', maxWidth: 640, margin: '0 auto 32px' }}>
                Add every room type you offer — Deluxe, Suite, Dormitory and so on. Each one is sent for approval when you complete the listing.
              </p>

              {rooms.length > 1 && (
                <div style={{ display: 'grid', gap: 12, marginBottom: 24 }}>
                  {rooms.map((room, index) => {
                    if (index === activeRoomIndex) return null;
                    const errors = roomsValidation.perRoom[index] || {};
                    const hasErrors = Object.keys(errors).length > 0;
                    const firstRate = room.occupancyRates?.['1guest'];
                    const countN = Number(room.roomCount) || 1;
                    const occN = Number(room.maxOccupancy) || 1;
                    return (
                      <div
                        key={room.id}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '14px 18px', borderRadius: 14, background: '#fff', border: `2px solid ${hasErrors && step6Touched ? '#fca5a5' : '#e2e8f0'}` }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                          <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fff7ed', color: '#FF7700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <FontAwesomeIcon icon={faBed} />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {room.roomTypeName || `Room type ${index + 1}`}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: hasErrors ? '#dc2626' : '#64748b' }}>
                              {hasErrors
                                ? 'Incomplete — click Edit to finish'
                                : `${countN} room${countN === 1 ? '' : 's'} • up to ${occN} guest${occN === 1 ? '' : 's'}${firstRate ? ` • from ₹${firstRate}/night` : ''}`}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            type="button"
                            onClick={() => switchActiveRoom(index)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1.5px solid #FF7700', background: '#fff', color: '#c2410c', fontWeight: 600, cursor: 'pointer' }}
                          >
                            <FontAwesomeIcon icon={faPen} size="sm" /> Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveRoomType(index)}
                            aria-label={`Remove ${room.roomTypeName || `room type ${index + 1}`}`}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 600, cursor: 'pointer' }}
                          >
                            <FontAwesomeIcon icon={faTrashAlt} size="sm" /> Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{ display: 'grid', gap: '32px', marginBottom: '24px' }}>
                <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', border: `2px solid ${rooms.length > 1 ? '#FF7700' : '#e2e8f0'}`, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e293b', margin: 0 }}>
                      {rooms.length > 1 ? `Room Type ${activeRoomIndex + 1} of ${rooms.length}` : 'Room Type Details'}
                    </h3>
                    {rooms.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveRoomType(activeRoomIndex)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 600, cursor: 'pointer' }}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} size="sm" /> Remove this room type
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
                    <div ref={roomSearchRef} style={{ position: 'relative', gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, color: '#334155', marginBottom: 8 }}>
                        Room Type Name *
                      </label>
                      <div style={{ position: 'relative' }}>
                        <FontAwesomeIcon
                          icon={faHotel}
                          style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 14 }}
                        />
                        <input
                          type="text"
                          value={activeRoom.roomTypeName}
                          autoComplete="off"
                          placeholder="Search room type (e.g. Deluxe)"
                          onFocus={() => {
                            if (roomSearchResults.length > 0 && !activeRoom.roomTypeCategoryId) setRoomSearchOpen(true);
                          }}
                          onChange={(e) => {
                            updateActiveRoom({ roomTypeName: e.target.value, roomTypeCategoryId: '' });
                            setRoomSearchOpen(true);
                          }}
                          style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            padding: '12px 14px 12px 40px',
                            borderRadius: 10,
                            border: `2px solid ${step6Touched && step6LiveErrors.roomTypeName ? '#dc2626' : activeRoom.roomTypeCategoryId ? '#16a34a' : '#e2e8f0'}`,
                            fontSize: '1rem',
                            outline: 'none',
                          }}
                        />
                        {roomSearchLoading && (
                          <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#94a3b8' }}>
                            Searching…
                          </span>
                        )}
                      </div>
                      {step6Touched && step6LiveErrors.roomTypeName && (
                        <span style={{ display: 'block', color: '#dc2626', fontSize: '0.85rem', marginTop: 6 }}>{step6LiveErrors.roomTypeName}</span>
                      )}
                      {activeRoom.roomTypeCategoryId ? (
                        <span style={{ display: 'block', color: '#16a34a', fontSize: '0.8rem', marginTop: 6, fontWeight: 600 }}>
                          Room type selected
                        </span>
                      ) : (
                        <span style={{ display: 'block', color: '#94a3b8', fontSize: '0.8rem', marginTop: 6 }}>
                          Type at least 2 letters and pick from the list
                        </span>
                      )}
                      {roomSearchOpen && roomSearchResults.length > 0 && !activeRoom.roomTypeCategoryId && (
                        <div
                          style={{
                            position: 'absolute',
                            zIndex: 40,
                            left: 0,
                            right: 0,
                            top: '100%',
                            marginTop: 6,
                            maxHeight: 280,
                            overflowY: 'auto',
                            background: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: 12,
                            boxShadow: '0 12px 32px rgba(15,23,42,0.12)',
                          }}
                        >
                          {roomSearchResults.map((item) => (
                            <button
                              key={item.category_id}
                              type="button"
                              onClick={() => {
                                updateActiveRoom({
                                  roomTypeName: item.category_name || '',
                                  roomTypeCategoryId: String(item.category_id),
                                  roomTypeSafeName: item.safe_name || '',
                                });
                                setRoomSearchOpen(false);
                                setRoomSearchResults([]);
                              }}
                              style={{
                                display: 'block',
                                width: '100%',
                                textAlign: 'left',
                                border: 'none',
                                background: 'transparent',
                                padding: '12px 14px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f1f5f9',
                                fontWeight: 700,
                                color: '#0f172a',
                                fontSize: '0.95rem',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = '#fff7ed'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                              {item.category_name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {renderField('roomCount', 'Number of Rooms *', activeRoom.roomCount, (v) => updateActiveRoom({ roomCount: v }), 'number', faBuilding, step6Touched && step6LiveErrors.roomCount, '1')}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, color: '#334155', marginBottom: 8 }}>
                        Max Occupancy *
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={20}
                        value={activeRoom.maxOccupancy}
                        onChange={(e) => {
                          const val = e.target.value;
                          updateActiveRoom((room) => ({
                            maxOccupancy: val,
                            occupancyRates: resizeOccupancyRates(room.occupancyRates, val),
                          }));
                        }}
                        placeholder="Guests per room"
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          padding: '12px 14px',
                          borderRadius: 10,
                          border: `2px solid ${step6Touched && step6LiveErrors.maxOccupancy ? '#dc2626' : '#e2e8f0'}`,
                          fontSize: '1rem',
                          outline: 'none',
                        }}
                      />
                      {step6Touched && step6LiveErrors.maxOccupancy && (
                        <span style={{ display: 'block', color: '#dc2626', fontSize: '0.85rem', marginTop: 6 }}>{step6LiveErrors.maxOccupancy}</span>
                      )}
                    </div>
                  </div>

                  {parseInt(activeRoom.maxOccupancy, 10) >= 1 && (
                    <div style={{ marginTop: 24 }}>
                      <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', margin: '0 0 16px 0' }}>
                        Price per occupancy (₹ / night) *
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 }}>
                        {Array.from({ length: Math.max(1, Math.min(20, parseInt(activeRoom.maxOccupancy, 10) || 1)) }, (_, i) => {
                          const guestN = i + 1;
                          const key = `${guestN}guest`;
                          const err =
                            (step6Touched && step6LiveErrors[`rate_${guestN}`]) ||
                            rateBlurWarnings[guestN];
                          return (
                            <div key={key}>
                              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                                {guestN} Guest{guestN > 1 ? 's' : ''}
                              </label>
                              <input
                                type="number"
                                min={1}
                                value={activeRoom.occupancyRates?.[key] || ''}
                                onChange={(e) => {
                                  const nextRates = applyOccupancyRateChange(
                                    activeRoom.occupancyRates,
                                    guestN,
                                    e.target.value,
                                    activeRoom.maxOccupancy
                                  );
                                  updateActiveRoom({ occupancyRates: nextRates });
                                  refreshRateBlurWarnings(nextRates, activeRoom.maxOccupancy);
                                }}
                                onBlur={() => refreshRateBlurWarnings()}
                                placeholder="₹"
                                style={{
                                  width: '100%',
                                  boxSizing: 'border-box',
                                  padding: '12px 14px',
                                  borderRadius: 10,
                                  border: `2px solid ${err ? '#dc2626' : '#e2e8f0'}`,
                                  fontSize: '1rem',
                                  outline: 'none',
                                }}
                              />
                              {err && (
                                <span style={{ display: 'block', color: '#dc2626', fontSize: '0.75rem', marginTop: 4 }}>{err}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', border: '2px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                  <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                    Room Amenities
                  </label>
                  {renderCheckboxGrid(
                    roomAmenitiesOptions,
                    activeRoom.selectedRoomAmenities || [],
                    (updater) => updateActiveRoom((room) => ({
                      selectedRoomAmenities: typeof updater === 'function' ? updater(room.selectedRoomAmenities || []) : updater,
                    }))
                  )}
                </div>

                <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', border: '2px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                    <div>
                      <h3 style={{ fontSize: '1.15rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>
                        Room Images {activeRoom.roomTypeName ? `— ${activeRoom.roomTypeName}` : ''}
                      </h3>
                      <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                        Photos of this room type. Uploaded with the property when you complete the listing.
                      </p>
                    </div>
                    <span style={{ fontSize: '0.85rem', fontWeight: 700, color: ((roomImages[activeRoom.id] || []).length) ? '#16a34a' : '#94a3b8' }}>
                      {(roomImages[activeRoom.id] || []).length ? `${(roomImages[activeRoom.id] || []).length} selected` : 'None selected'}
                    </span>
                  </div>

                  <input
                    ref={(el) => { roomImageInputRefs.current[activeRoom.id] = el; }}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => { addRoomImages(activeRoom.id, e.target.files); e.target.value = ''; }}
                  />

                  <div
                    role="button"
                    tabIndex={0}
                    aria-label={`Add room images for ${activeRoom.roomTypeName || 'this room type'}`}
                    onClick={() => openRoomImagePicker(activeRoom.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openRoomImagePicker(activeRoom.id); } }}
                    style={{
                      border: '2px dashed #cbd5e1',
                      background: '#f8fafc',
                      borderRadius: 12,
                      padding: '20px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <FontAwesomeIcon icon={faCloudUploadAlt} size="2x" color="#FF7700" />
                    <div style={{ marginTop: 8, fontWeight: 600, color: '#334155' }}>Click to choose room images or drag & drop them here</div>
                    <div style={{ marginTop: 4, fontSize: '0.8rem', color: '#94a3b8' }}>JPG, PNG or WEBP</div>
                  </div>

                  {(roomImages[activeRoom.id] || []).length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12, marginTop: 16 }}>
                      {(roomImages[activeRoom.id] || []).map((img) => (
                        <div key={img.id} style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0', background: '#fff' }}>
                          <img src={img.previewUrl} alt={img.name} style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />
                          <button
                            type="button"
                            onClick={() => removeRoomImage(activeRoom.id, img.id)}
                            aria-label={`Remove ${img.name}`}
                            style={{ position: 'absolute', top: 6, right: 6, width: 26, height: 26, borderRadius: '50%', border: 'none', background: 'rgba(15,23,42,0.75)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            <FontAwesomeIcon icon={faTimes} size="xs" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', border: '2px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e293b', margin: '0 0 20px 0' }}>Rate / inventory dates</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                    {renderField('rateStartDate', 'Start Date *', activeRoom.rateStartDate, (v) => updateActiveRoom({ rateStartDate: v }), 'date', null, step6Touched && step6LiveErrors.rateStartDate)}
                    {renderField('rateEndDate', 'End Date *', activeRoom.rateEndDate, (v) => updateActiveRoom({ rateEndDate: v }), 'date', null, step6Touched && step6LiveErrors.rateEndDate)}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                <button
                  type="button"
                  onClick={handleAddRoomType}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 10, border: '2px dashed #FF7700', background: '#fff7ed', color: '#c2410c', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' }}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add another room type
                </button>
              </div>

              {step6Touched && !roomsValidation.ok && (
                <div style={{ textAlign: 'center', marginTop: '16px', color: '#dc2626', fontSize: '0.9rem', fontWeight: 600 }}>
                  {roomsValidation.firstInvalidIndex === activeRoomIndex || roomsValidation.firstInvalidIndex < 0
                    ? 'Please complete the highlighted fields above to continue.'
                    : `Room type ${roomsValidation.firstInvalidIndex + 1} is incomplete. Click Edit to finish it.`}
                </div>
              )}
              {continueButton(STEPS.ROOMS, 'Continue to Verification')}
            </div>
          )}

          {/* Step 7: Property Images (required; uploaded after the property is created) */}
          {currentStep === STEPS.IMAGES && (
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
              <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#333', textAlign: 'center', marginBottom: '12px' }}>
                Property Images
              </h2>
              <p style={{ textAlign: 'center', color: '#64748b', fontSize: '1rem', maxWidth: 640, margin: '0 auto 32px' }}>
                Listings with photos get far more bookings. A main image and at least 5 property images are required to complete your listing.
              </p>

              {step7Touched && Object.keys(step7Errors).length > 0 && (
                <div style={{ textAlign: 'center', marginBottom: '16px', color: '#dc2626', fontSize: '0.9rem', fontWeight: 600 }}>
                  Please complete the required images above to continue.
                </div>
              )}

              <div style={{ display: 'grid', gap: '24px', marginBottom: '24px' }}>
                {HOTEL_IMAGE_SECTIONS.map((section) => {
                  const images = pendingImages[section.key] || [];
                  const isDragOver = dragOverSection === section.key;
                  return (
                    <div key={section.key} style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', border: '2px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                        <div>
                          <h3 style={{ fontSize: '1.15rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>{section.label} <span style={{ color: '#dc2626' }}>*</span></h3>
                          <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>{section.hint}</p>
                        </div>
                        <span style={{ fontSize: '0.85rem', fontWeight: 700, color: images.length >= (section.min || 1) ? '#16a34a' : '#dc2626' }}>
                          {images.length}/{section.min || 1} selected
                        </span>
                      </div>

                      <input
                        ref={(el) => { imageInputRefs.current[section.key] = el; }}
                        type="file"
                        accept="image/*"
                        multiple={section.multiple}
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          addImagesToSection(section.key, e.target.files);
                          e.target.value = '';
                        }}
                      />

                      <div
                        role="button"
                        tabIndex={0}
                        aria-label={`Add ${section.label}`}
                        onClick={() => openImagePicker(section.key)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openImagePicker(section.key);
                          }
                        }}
                        onDragOver={(e) => { e.preventDefault(); setDragOverSection(section.key); }}
                        onDragLeave={() => setDragOverSection('')}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragOverSection('');
                          addImagesToSection(section.key, e.dataTransfer.files);
                        }}
                        style={{
                          border: `2px dashed ${isDragOver ? '#FF7700' : '#cbd5e1'}`,
                          background: isDragOver ? 'rgba(255,119,0,0.06)' : '#f8fafc',
                          borderRadius: 12,
                          padding: '24px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <FontAwesomeIcon icon={faCloudUploadAlt} size="2x" color="#FF7700" />
                        <div style={{ marginTop: 10, fontWeight: 600, color: '#334155' }}>
                          {section.multiple
                            ? 'Click to choose images or drag & drop them here'
                            : images.length
                              ? 'Click to replace the main image'
                              : 'Click to choose the main image or drag & drop it here'}
                        </div>
                        <div style={{ marginTop: 4, fontSize: '0.8rem', color: '#94a3b8' }}>JPG, PNG or WEBP</div>
                      </div>

                      {images.length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginTop: 16 }}>
                          {images.map((img) => (
                            <div key={img.id} style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0', background: '#fff' }}>
                              <img src={img.previewUrl} alt={img.name} style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }} />
                              <button
                                type="button"
                                onClick={() => removeImage(section.key, img.id)}
                                aria-label={`Remove ${img.name}`}
                                style={{ position: 'absolute', top: 6, right: 6, width: 26, height: 26, borderRadius: '50%', border: 'none', background: 'rgba(15,23,42,0.75)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              >
                                <FontAwesomeIcon icon={faTimes} size="xs" />
                              </button>
                              <div title={img.name} style={{ padding: '6px 8px', fontSize: '0.75rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {img.name} · {formatBytes(img.size)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div style={{ padding: '12px 16px', borderRadius: 12, background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', fontSize: '0.9rem', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <FontAwesomeIcon icon={faImages} style={{ marginTop: 3 }} />
                <span>
                  {totalPendingImages > 0
                    ? `${totalPendingImages} image${totalPendingImages === 1 ? '' : 's'} will be uploaded when you complete the listing.`
                    : 'No images selected yet.'}
                  {' '}Selected images are kept only until you leave or refresh this page.
                </span>
              </div>

              {continueButton(STEPS.IMAGES, 'Continue to Room Setup')}
            </div>
          )}

          {/* Step 8: KYC & Compliance */}
          {currentStep === STEPS.KYC && (
            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
              <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#333', textAlign: 'center', marginBottom: '12px' }}>
                Verification & Compliance
              </h2>
              <p style={{ textAlign: 'center', color: '#64748b', fontSize: '1rem', maxWidth: 600, margin: '0 auto 32px' }}>
                Complete a quick verification to publish your listing. Your details are stored securely.
              </p>

              <div style={{ display: 'grid', gap: '24px', marginBottom: '24px' }}>
                {/* PAN / GST (both optional, GST verifiable) */}
                <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', border: '2px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                  <h3 style={{ fontSize: '1.15rem', fontWeight: 600, color: '#1e293b', margin: '0 0 4px 0' }}>
                    <FontAwesomeIcon icon={faIdCard} style={{ marginRight: 8, color: '#FF7700' }} />
                    Finance — PAN or GST
                  </h3>
                  <p style={{ margin: '0 0 20px', fontSize: '0.85rem', color: '#64748b' }}>Optional. Add your PAN or GST number for faster payouts and verification.</p>

                  {/* GST input with Verify button */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      GST Number
                      <span style={{ color: '#eab308', fontSize: '0.72rem', fontWeight: 600, marginLeft: 8, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                      {gstVerifiedData && <span style={{ color: '#16a34a', fontSize: '0.75rem', marginLeft: '8px', fontWeight: 700 }}>✓ Verified</span>}
                    </label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <FontAwesomeIcon icon={faFileSignature} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 14 }} />
                        <input
                          type="text"
                          value={gstNumber}
                          onChange={(e) => { setGstNumber(e.target.value.toUpperCase()); if (gstLocked) handleGstUnlock(); }}
                          placeholder="22ABCDE1234F1Z5"
                          disabled={gstLocked}
                          style={{
                            width: '100%', padding: '14px 14px 14px 40px', fontSize: '1rem',
                            border: `2px solid ${gstVerifiedData ? '#16a34a' : gstError ? '#dc2626' : '#e2e8f0'}`,
                            borderRadius: '10px', outline: 'none', transition: 'all 0.3s ease',
                            backgroundColor: gstLocked ? '#f0fdf4' : '#f8fafc',
                          }}
                        />
                      </div>
                      {!gstLocked && (
                        <button
                          type="button"
                          onClick={handleVerifyGst}
                          disabled={gstVerifying || !gstNumber.trim()}
                          style={{
                            whiteSpace: 'nowrap', padding: '0 20px', fontSize: '0.9rem', fontWeight: 600,
                            borderRadius: '10px', border: 'none',
                            cursor: gstVerifying || !gstNumber.trim() ? 'not-allowed' : 'pointer',
                            background: gstVerifying ? '#cbd5e1' : 'linear-gradient(135deg, #FF7700, #FF8800)',
                            color: '#000',
                          }}
                        >
                          {gstVerifying ? 'Verifying...' : 'Verify'}
                        </button>
                      )}
                      {gstLocked && (
                        <button
                          type="button"
                          onClick={handleGstUnlock}
                          style={{
                            whiteSpace: 'nowrap', padding: '0 20px', fontSize: '0.9rem', fontWeight: 600,
                            borderRadius: '10px', border: '1.5px solid #e2e8f0', background: '#fff',
                            color: '#64748b', cursor: 'pointer',
                          }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                    {gstError && <span style={{ display: 'block', color: '#dc2626', fontSize: '0.85rem', marginTop: '6px' }}>{gstError}</span>}
                  </div>

                  {/* PAN input (auto-filled & locked when GST verified) */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      PAN Number
                      <span style={{ color: '#eab308', fontSize: '0.72rem', fontWeight: 600, marginLeft: 8, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                      {gstLocked && <span style={{ color: '#64748b', fontSize: '0.75rem', marginLeft: '8px', fontWeight: 500 }}>(auto-filled from GST)</span>}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <FontAwesomeIcon icon={faIdCard} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 14 }} />
                      <input
                        type="text"
                        value={panNumber}
                        onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
                        placeholder="ABCDE1234F"
                        disabled={gstLocked}
                        style={{
                          width: '100%', padding: '14px 14px 14px 40px', fontSize: '1rem',
                          border: `2px solid ${gstLocked ? '#16a34a' : '#e2e8f0'}`,
                          borderRadius: '10px', outline: 'none', transition: 'all 0.3s ease',
                          backgroundColor: gstLocked ? '#f0fdf4' : '#f8fafc',
                        }}
                      />
                    </div>
                  </div>

                  {/* GST verified info display */}
                  {gstVerifiedData && (
                    <div style={{ marginTop: '16px', padding: '16px', borderRadius: '12px', background: '#f0fdf4', border: '2px solid #bbf7d0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#16a34a' }} />
                        <span style={{ fontWeight: 700, color: '#14532d', fontSize: '0.95rem' }}>GST Verified — Active</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                        <div><span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>Legal Name</span><br /><span style={{ fontSize: '0.9rem', color: '#1e293b' }}>{gstVerifiedData.legal_name || '—'}</span></div>
                        <div><span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>Trade Name</span><br /><span style={{ fontSize: '0.9rem', color: '#1e293b' }}>{gstVerifiedData.trade_name || '—'}</span></div>
                        <div><span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>Constitution</span><br /><span style={{ fontSize: '0.9rem', color: '#1e293b' }}>{gstVerifiedData.constitution || '—'}</span></div>
                        <div><span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>State</span><br /><span style={{ fontSize: '0.9rem', color: '#1e293b' }}>{gstVerifiedData.state || '—'}</span></div>
                        <div><span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>District</span><br /><span style={{ fontSize: '0.9rem', color: '#1e293b' }}>{gstVerifiedData.district || '—'}</span></div>
                        <div><span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>Pincode</span><br /><span style={{ fontSize: '0.9rem', color: '#1e293b' }}>{gstVerifiedData.pincode || '—'}</span></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Agree to policy */}
                <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', border: `2px solid ${kycTouched && kycErrors.agreedToPolicy ? '#dc2626' : '#e2e8f0'}`, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', padding: '12px 14px', borderRadius: '12px', background: agreedToPolicy ? '#f0fdf4' : '#f8fafc', border: `2px solid ${agreedToPolicy ? '#16a34a' : kycTouched && kycErrors.agreedToPolicy ? '#dc2626' : '#e2e8f0'}` }}>
                    <input type="checkbox" checked={agreedToPolicy} onChange={(e) => setAgreedToPolicy(e.target.checked)}
                      style={{ width: '20px', height: '20px', marginTop: '2px', accentColor: '#16a34a', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.95rem', color: '#1e293b', lineHeight: 1.5 }}>
                      I have read and agree to the <a href="/terms-conditions" target="_blank" rel="noopener noreferrer" style={{ color: '#FF7700', fontWeight: 600 }}>Terms & Conditions</a> and <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#FF7700', fontWeight: 600 }}>Privacy Policy</a>. I confirm the information provided is accurate.
                    </span>
                  </label>
                  {kycTouched && kycErrors.agreedToPolicy && <span style={{ display: 'block', color: '#dc2626', fontSize: '0.85rem', marginTop: '8px' }}>{kycErrors.agreedToPolicy}</span>}
                </div>
              </div>

              {kycTouched && Object.keys(kycErrors).length > 0 && (
                <div style={{ textAlign: 'center', marginBottom: '16px', color: '#dc2626', fontSize: '0.9rem', fontWeight: 600 }}>
                  Please complete the {Object.keys(kycErrors).length} highlighted requirement{Object.keys(kycErrors).length > 1 ? 's' : ''} above to continue.
                </div>
              )}
              {continueButton(STEPS.KYC, 'Continue to Review & Submit')}
            </div>
          )}

          {/* Step 8: Review & Submit */}
          {currentStep === STEPS.REVIEW && (
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
              <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#333', textAlign: 'center', marginBottom: '16px' }}>
                Review & Submit
              </h2>
              <p style={{ textAlign: 'center', color: '#64748b', fontSize: '1rem', marginBottom: '40px' }}>
                Please review your listing details before completing.
              </p>

              <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', border: '2px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)', marginBottom: '40px' }}>
                {buildOnboardingSummary(wizardState).map((item, index) => (
                  <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ color: '#666', fontSize: '13px', fontWeight: 600 }}>{item.label}</span>
                    <span style={{ color: '#333', fontSize: '14px', fontWeight: 500, textAlign: 'right', flex: 1, marginLeft: '16px' }}>{item.value}</span>
                  </div>
                ))}
              </div>

              {submitError && (
                <div style={{ marginBottom: '16px', textAlign: 'center', color: '#dc2626', fontSize: '0.95rem', fontWeight: 600 }}>
                  {submitError}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                <button
                  onClick={handlePreviousStep}
                  disabled={submitting}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#64748b',
                    border: '2px solid #e2e8f0',
                    padding: '14px 32px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    borderRadius: '10px',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => { if (!submitting) { e.target.style.backgroundColor = '#f8fafc'; e.target.style.borderColor = '#cbd5e1'; }}}
                  onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = '#e2e8f0'; }}
                >
                  Back
                </button>
                <SubmitButton loading={submitting} disabled={!isStep6Valid || !isStep7Valid || !isKycValid} onClick={handleCompleteListing}>
                  {submitting
                    ? imageUploadProgress > 0
                      ? `Uploading images ${imageUploadProgress}%`
                      : 'Creating your listing…'
                    : 'Complete Listing'}
                </SubmitButton>
              </div>
            </div>
          )}

            </div>
          </div>

        </div>

        <ListingSupportBox />
      </div>
    </div>
  );
};

export default ListingPage;
